import { useState, useContext, useCallback } from 'react';
import * as Sentry from '@sentry/browser';

import FirebaseContext from '../context/FirebaseContext';
import CheckoutSessionOnboarding, { CheckoutSessionOnboardingStatus } from '../Model/CheckoutSessionOnboarding';
import useLogger from './useLogger';

const ONBOARDING_SESSION_SEPARATOR = '-';
const ONBOARDING_SESSION_KEY = 'onboardingSession';
/*
  Sentry groups by the error message, so one string for every failure collapsed 400/403/404/500 into a
  single undiagnosable issue. Both throw sites share this prefix and each add what tells them apart.
*/
const CHECKOUT_SESSION_ERROR = 'subscription-createCheckoutSession failed';

/**
 * Hook to handle Stripe checkout redirect functionality
 * Fetches the Stripe checkout URL from the cloud function and manages redirect
 */
const useStripeCheckoutPage = () => {
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { logEvent } = useLogger();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  /**
   * Gets the Stripe checkout URL from the cloud function
   * @param {string} contractId - The contract ID associated with the user
   * @param {string} sessionId - The Stripe checkout session ID - optional
   * @param {Date|null} startAtDate - Start date from contract with the user timestamp
   * @param {string} leadId - Lead this checkout came from, when the link named one
   * @returns {Promise<{url: string|null, error: Error|null}>}
   */
  const getStripeCheckoutUrl = useCallback(async (contractId, sessionId = '', startAtDate = null, leadId = '') => {
    /*
      Held outside the try because the report is built in the catch, which also covers a failed
      onboarding read and a dead network — neither of those sets either value.
    */
    let responseStatus;
    let serverError;
    try {
      // If both contract and session ids are provided, fetch checkout session onboarding document
      if (contractId && sessionId) {
        const checkoutSessionOnboardingCollection = await CheckoutSessionOnboarding.getByContractAndSessionIds(
          contractId,
          sessionId,
        );
        if (checkoutSessionOnboardingCollection.hasDocs) {
          const checkoutSessionOnboardingDoc = checkoutSessionOnboardingCollection.docs[0];
          const { status, urlExpiresAt, url: docCheckoutUrl } = checkoutSessionOnboardingDoc;
          if (status === CheckoutSessionOnboardingStatus.CREATED) {
            // Check if url is expired
            if (urlExpiresAt && urlExpiresAt > new Date()) {
              // Return url and session id
              return { url: docCheckoutUrl, sessionId, error: null };
            }
          }
        }
      }
      // For any other status, or if url is expired, create a new checkout session
      // We should pass the start at with the user timestamp the same way we do for the normal subscription flow
      const startAt = startAtDate?.valueOf();
      const response = await remote(
        'subscription-createCheckoutSession',
        {
          contractId, sessionId, startAt, webPageUrl: window.location.href, leadId,
        },
        {},
        true,
      );

      responseStatus = response.status;

      /*
        The server sends its reason at the top level, alongside `success` — not inside `data`.

        Parsed defensively because `remote` hands back the raw fetch Response: a crashed or
        undeployed function answers HTML, and an unguarded `json()` throws a SyntaxError before the
        status-tagged message below can form — leaving Sentry to group by the body shape on exactly
        the 5xx failures this file separates. An unparseable body is reported as one instead of
        leaving `serverError` blank.
      */
      const { success, data, error: responseError } = await response.json()
        .catch(() => ({ error: 'non-JSON response body' }));
      serverError = responseError;
      if (!success || !data?.url) {
        throw new Error(`${CHECKOUT_SESSION_ERROR} (${responseStatus})`);
      }

      return { url: data.url, sessionId: data.sessionId, error: null };
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          component: 'useStripeCheckoutPage',
          type: 'api_error',
        },
        extra: {
          contractId,
          status: responseStatus,
          serverError,
        },
      });
      setError(true);
      setIsLoading(false);
      return { url: null, sessionId: null, error: err };
    }
  }, [remote]);

  /**
   * Redirects to the Stripe checkout URL. It never rejects: every failure is reported to Sentry once,
   * from this hook, and surfaces through `error`.
   * @param {string} contractId - The contract ID associated with the user
   * @param {Date|null} startAtDate - Start date from contract with the user timestamp
   * @param {string} leadId - Lead this checkout came from, when the link named one
   */
  const redirectToStripeCheckout = useCallback(async (contractId, startAtDate = null, leadId = '') => {
    setIsLoading(true);
    setError(false);
    try {
      let sessionId = null;
      const onboardingSessionItem = window.localStorage.getItem(ONBOARDING_SESSION_KEY);
      if (onboardingSessionItem) {
        // onboardingSessionItem is a string that contains both a contract and checkout session id
        // this string is stored in the format contractId-sessionId
        const [localContractId, localSessionId] = onboardingSessionItem.split(ONBOARDING_SESSION_SEPARATOR);
        // If the local contract id is the same as the contract id, use the local session id
        if (localContractId === contractId) {
          sessionId = localSessionId;
        }
      }
      const { url, sessionId: finalSessionId, error: urlError } = await getStripeCheckoutUrl(
        contractId,
        sessionId,
        startAtDate,
        leadId,
      );
      // Already reported, with the server's status and reason, and flagged by getStripeCheckoutUrl.
      if (urlError) {
        return;
      }
      if (!url) {
        throw new Error(`${CHECKOUT_SESSION_ERROR} (no checkout url)`);
      }

      // Save contract id and session id to local storage
      window.localStorage.setItem(
        ONBOARDING_SESSION_KEY,
        `${contractId}${ONBOARDING_SESSION_SEPARATOR}${finalSessionId}`,
      );
      // Track redirect to Stripe hosted checkout
      logEvent('stripeHostedCheckoutRedirected', { contractId });

      // Redirect to url gotten from the cloud function, can be stripe checkout or success page url
      window.location.href = url;
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          component: 'useStripeCheckoutPage',
          type: 'redirect_error',
        },
        extra: {
          contractId,
        },
      });
      setError(true);
      setIsLoading(false);
    }
  }, [getStripeCheckoutUrl, logEvent]);

  return {
    redirectToStripeCheckout,
    isLoading,
    error,
  };
};

export default useStripeCheckoutPage;
